import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
    Box,
    Button,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
    useTheme
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import ApiClient from '../../config/ApiClient';
import { CoachContent, LessonPlan, LessonPlans } from '../../object-actions/types/types';
import LessonPlanCard from './LessonPlanCard';

interface LessonPlanRendererProps {
    entity: CoachContent;
}

const LessonPlanRenderer: React.FC<LessonPlanRendererProps> = ({ entity }) => {
    const theme = useTheme();
    const [activePlanIndex, setActivePlanIndex] = useState(0);
    const [expandedPlans, setExpandedPlans] = useState<Set<number>>(new Set([0])); // Start with first plan expanded
    const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [lessonPlanInstance, setLessonPlanInstance] = useState<LessonPlans | null>(null);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingPlanIndex, setRecordingPlanIndex] = useState<number | null>(null);
    const [showChangeLessonDialog, setShowChangeLessonDialog] = useState(false);
    const [pendingPlanIndex, setPendingPlanIndex] = useState<number | null>(null);
    const [loadingLessonPlan, setLoadingLessonPlan] = useState(true);

    // Fetch LessonPlans instance for this CoachContent
    useEffect(() => {
        const fetchLessonPlan = async () => {
            if (entity.purpose !== 'lesson_plan' || !entity.id) {
                setLoadingLessonPlan(false);
                return;
            }

            try {
                // Use by_coach_content endpoint to get or create LessonPlans instance
                const response = await ApiClient.get<LessonPlans>(
                    `/api/lesson-plans/by_coach_content/?coach_content=${entity.id}`
                );

                if (response.success && response.data) {
                    setLessonPlanInstance(response.data);
                }
            } catch (error) {
                console.error('Error fetching lesson plan instance:', error);
            } finally {
                setLoadingLessonPlan(false);
            }
        };

        fetchLessonPlan();
    }, [entity.id, entity.purpose]);

    if (entity.purpose !== 'lesson_plan' || !entity.json_body) {
        // THIS SHOULD NEVER HAPPEN
        return (
            <Box>
                {entity.body ? (
                    <ReactMarkdown>{entity.body}</ReactMarkdown>
                ) : (
                    <Typography variant="body1" color="text.secondary">
                        No content available at this time.
                    </Typography>
                )}
            </Box>
        );
    }

    // Validate and parse json_body
    let lessonPlans: LessonPlan[] = [];
    try {
        const jsonBody = typeof entity.json_body === 'string'
            ? JSON.parse(entity.json_body)
            : entity.json_body;

        if (Array.isArray(jsonBody)) {
            lessonPlans = jsonBody;
        } else if (jsonBody && typeof jsonBody === 'object') {
            // Handle single lesson plan object
            lessonPlans = [jsonBody.root as LessonPlan];
        }
    } catch (error) {
        console.error('Error parsing lesson plan json_body:', error);
        // Fallback to markdown
        return (
            <Box>
                <Typography variant="body1" color="error" sx={{ mb: 2 }}>
                    Error parsing lesson plan data. Displaying fallback content.
                </Typography>
                {entity.body && (
                    <Box
                        sx={{
                            '& p': { margin: 0 },
                            '& p + p': { marginTop: 2 }
                        }}
                    >
                        <ReactMarkdown>{entity.body}</ReactMarkdown>
                    </Box>
                )}
            </Box>
        );
    }

    if (lessonPlans.length === 0) {
        // Fallback to markdown
        return (
            <Box>
                {entity.body ? (
                    <Box
                        sx={{
                            '& p': { margin: 0 },
                            '& p + p': { marginTop: 2 }
                        }}
                    >
                        <ReactMarkdown>{entity.body}</ReactMarkdown>
                    </Box>
                ) : (
                    <Typography variant="body1" color="text.secondary">
                        No lesson plan content available.
                    </Typography>
                )}
            </Box>
        );
    }

    const scrollToCard = (index: number) => {
        const cardElement = cardRefs.current[index];
        if (cardElement) {
            // Get the position of the element
            const elementTop = cardElement.getBoundingClientRect().top;
            const offset = 20; // Small offset to ensure header is visible
            const scrollPosition = window.scrollY + elementTop - offset;

            window.scrollTo({
                top: scrollPosition,
                behavior: 'smooth',
            });
        }
    };

    const handlePlanChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newIndex = Number(event.target.value);

        // Check if currently recording
        if (isRecording && recordingPlanIndex !== null && recordingPlanIndex !== newIndex) {
            setPendingPlanIndex(newIndex);
            setShowChangeLessonDialog(true);
            return;
        }

        setActivePlanIndex(newIndex);
        // Expand the selected plan
        setExpandedPlans(prev => new Set([...prev, newIndex]));
        // Scroll to the selected card after state update
        setTimeout(() => scrollToCard(newIndex), 0);
    };

    const handleAccordionChange = (planIndex: number) => (
        event: React.SyntheticEvent<Element, Event>,
        expanded: boolean
    ) => {
        if (expanded) {
            // Check if currently recording
            if (isRecording && recordingPlanIndex !== null && recordingPlanIndex !== planIndex) {
                setPendingPlanIndex(planIndex);
                setShowChangeLessonDialog(true);
                return;
            }

            // Add to expanded set
            setExpandedPlans(prev => new Set([...prev, planIndex]));
            setActivePlanIndex(planIndex);
            // Scroll to the expanded card
            setTimeout(() => scrollToCard(planIndex), 0);
        } else {
            // Remove from expanded set
            setExpandedPlans(prev => {
                const newSet = new Set(prev);
                newSet.delete(planIndex);
                return newSet;
            });
        }
    };

    const handleNext = () => {
        const newIndex = Math.min(activePlanIndex + 1, lessonPlans.length - 1);

        // Check if currently recording
        if (isRecording && recordingPlanIndex !== null && recordingPlanIndex !== newIndex) {
            setPendingPlanIndex(newIndex);
            setShowChangeLessonDialog(true);
            return;
        }

        setActivePlanIndex(newIndex);
        // Expand the plan when navigating to it
        setExpandedPlans(prev => new Set([...prev, newIndex]));
        setTimeout(() => scrollToCard(newIndex), 0);
    };

    const handlePrevious = () => {
        const newIndex = Math.max(activePlanIndex - 1, 0);

        // Check if currently recording
        if (isRecording && recordingPlanIndex !== null && recordingPlanIndex !== newIndex) {
            setPendingPlanIndex(newIndex);
            setShowChangeLessonDialog(true);
            return;
        }

        setActivePlanIndex(newIndex);
        // Expand the plan when navigating to it
        setExpandedPlans(prev => new Set([...prev, newIndex]));
        setTimeout(() => scrollToCard(newIndex), 0);
    };

    const handleRecordingChange = (recording: boolean) => {
        setIsRecording(recording);
        if (recording) {
            setRecordingPlanIndex(activePlanIndex);
        } else {
            setRecordingPlanIndex(null);
        }
    };

    const handleDialogStopAndUpload = () => {
        // The recorder will handle stopping and uploading automatically via onStop callback
        // We just need to wait for it to finish, then switch
        // For now, we'll just switch - the upload happens automatically
        setShowChangeLessonDialog(false);
        if (pendingPlanIndex !== null) {
            setActivePlanIndex(pendingPlanIndex);
            setExpandedPlans(prev => new Set([...prev, pendingPlanIndex]));
            setTimeout(() => scrollToCard(pendingPlanIndex), 0);
        }
        setPendingPlanIndex(null);
        // Note: The recording will continue until the recorder component stops it
        // This is a limitation - we'd need to expose a stop method from the recorder
    };

    const handleDialogDiscard = () => {
        // Discard recording - stop without upload
        setShowChangeLessonDialog(false);
        setIsRecording(false);
        setRecordingPlanIndex(null);
        if (pendingPlanIndex !== null) {
            setActivePlanIndex(pendingPlanIndex);
            setExpandedPlans(prev => new Set([...prev, pendingPlanIndex]));
            setTimeout(() => scrollToCard(pendingPlanIndex), 0);
        }
        setPendingPlanIndex(null);
    };

    const handleDialogCancel = () => {
        setShowChangeLessonDialog(false);
        setPendingPlanIndex(null);
    };

    return (
        <Box>
            {lessonPlans.length > 1 && (
                <Box
                    sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`
                    }}
                >
                    <Box sx={{
                        display: 'flex', alignItems: 'flex-end',
                        gap: 2, flexWrap: 'wrap'
                    }}>
                        <TextField
                            select
                            label="Lesson Plans"
                            variant="standard"
                            size="small"
                            sx={{ flexGrow: 1 }}
                            value={activePlanIndex}
                            onChange={handlePlanChange}
                        >
                            {lessonPlans.map((plan, index) => {
                                const label = plan.topic || plan.theme || `Plan ${index + 1}`;
                                return (
                                    <MenuItem key={index} value={index}>
                                        {label}
                                    </MenuItem>
                                );
                            })}
                        </TextField>
                        <ButtonGroup variant="outlined" aria-label="lesson plan navigation">
                            <Button
                                onClick={handlePrevious}
                                disabled={activePlanIndex === 0}
                                startIcon={<ArrowBackIcon />}
                            >
                                Previous
                            </Button>
                            <Button
                                onClick={handleNext}
                                disabled={activePlanIndex === lessonPlans.length - 1}
                                endIcon={<ArrowForwardIcon />}
                            >
                                Next
                            </Button>
                        </ButtonGroup>
                    </Box>
                </Box>
            )}

            {/* Render Lesson Plans as Collapsible Cards */}
            {lessonPlans.map((plan, index) => {
                const isActive = activePlanIndex === index;
                const isExpanded = expandedPlans.has(index);
                const lessonPlanId = lessonPlanInstance && !loadingLessonPlan
                    ? (typeof lessonPlanInstance.id === 'number' ? lessonPlanInstance.id : Number(lessonPlanInstance.id))
                    : null;

                return (
                    <Box key={index}>
                        <LessonPlanCard
                            plan={plan}
                            planIndex={index}
                            expanded={isExpanded}
                            onChange={handleAccordionChange(index)}
                            cardRef={(el: HTMLDivElement | null) => {
                                cardRefs.current[index] = el;
                            }}
                            lessonPlanId={lessonPlanId}
                            onRecordingChange={handleRecordingChange}
                            showRecorder={isActive}
                            coachContentId={entity.id ? (typeof entity.id === 'number' ? entity.id : Number(entity.id)) : null}
                        />
                    </Box>
                );
            })}

            {/* Dialog for changing lesson while recording */}
            <Dialog
                open={showChangeLessonDialog}
                onClose={handleDialogCancel}
                aria-labelledby="change-lesson-dialog-title"
                aria-describedby="change-lesson-dialog-description"
            >
                <DialogTitle id="change-lesson-dialog-title">
                    Recording in Progress
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="change-lesson-dialog-description">
                        You are currently recording. What would you like to do with the current recording?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogCancel} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleDialogDiscard} color="error">
                        Discard Recording
                    </Button>
                    <Button onClick={handleDialogStopAndUpload} color="primary" variant="contained">
                        Stop & Upload Recording
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LessonPlanRenderer;

